import qs from 'qs';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { faEnvelope, faMobileScreen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Anchor,
  Button,
  Group,
  PinInput,
  Stack,
  Text,
  Title,
} from '@cowbell/ui';
import { BrokerQuestionsTheme } from '@cowbell/broker/questions';

import {
  sendCodeToRetail,
  getRetailAccessToken,
  getRedirectUrl,
  resendPhoneCode,
} from '../../api/AuthService';
import { useActor } from '../hooks/useActor';
import { flowUserAuthenticated } from '../../store/actions/user.store.actions';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import { toColorScheme } from '../../personas/broker/adapters/colorScheme';
import type { RootStore } from '../../types';
import PublicLogo from './LogoHeader';
import {
  accessCodeIntro,
  codeSentMessage,
  type CodeChannel,
} from './accessCodeHeading';
import classes from './accessCode.module.css';

const CODE_LENGTH = 6;

interface FlowAccess {
  readonly flow?: string | null;
  readonly flowUserEmail?: string | null;
  readonly flowUserPhone?: string | null;
}

interface AccessCodeTempProps {
  readonly match: { readonly params: { readonly workflowId: string } };
}

/**
 * Where a flow link lands: the person gets a one-time code by email or text, then enters it here to reach the page the
 * link is for. Getting the code comes first, so it is step 1 and sits above the code boxes.
 */
export function AccessCodeTemp({ match }: AccessCodeTempProps) {
  const { workflowId } = match.params;
  const apiError = useAPIErrorHandler();
  const dispatch = useDispatch();
  const history = useHistory();
  const actor = useActor();
  const colorScheme = toColorScheme(
    useSelector((state: RootStore) => state.uiSettings.theme)
  );

  const [access, setAccess] = useState<FlowAccess | null>(null);
  const [sentBy, setSentBy] = useState<CodeChannel | null>(null);
  const [sending, setSending] = useState<CodeChannel | null>(null);
  const [code, setCode] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [redirectURL, setUrl] = useState('');

  React.useEffect(() => {
    if (!redirectURL) {
      return;
    }
    // An agency user lands on the page with their agency's name, except on a surplus notice, which takes none.
    if (actor.isAgency && access?.flow !== 'SURPLUS_NOTICE') {
      history.replace({
        pathname: redirectURL,
        search: qs.stringify(
          { agency: actor?.account?.name },
          { addQueryPrefix: true }
        ),
      });
      return;
    }
    history.replace(redirectURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectURL]);

  const requestCode = (channel: CodeChannel) => {
    setSending(channel);
    const request =
      channel === 'email'
        ? sendCodeToRetail(workflowId)
        : resendPhoneCode(workflowId);

    request
      .then((resp: { data: FlowAccess }) => {
        dispatch(flowUserAuthenticated(resp.data));
        setAccess(resp.data);
        setSentBy(channel);
      })
      .catch(apiError('Failed to send code'))
      .finally(() => setSending(null));
  };

  const logIn = (event: React.FormEvent) => {
    event.preventDefault();
    setLoggingIn(true);
    getRetailAccessToken({ workflowId, secret: code }).then(
      () =>
        // The code was accepted and is used up, so a failure from here on keeps the boxes as they are.
        getRedirectUrl(workflowId)
          .then((resp: { data: { redirectURL: string } }) =>
            setUrl(resp.data.redirectURL)
          )
          .catch((error: Error) => {
            setLoggingIn(false);
            apiError('Failed to get redirect URL')(error);
          }),
      (error: Error) => {
        setCode('');
        setLoggingIn(false);
        apiError('That code did not work. Check it, or get a new one.')(error);
      }
    );
  };

  // Text is offered until the server says there is no phone number to send it to.
  const noPhone = !!access && !access.flowUserPhone;

  return (
    <main className={classes.page}>
      <PublicLogo />
      <BrokerQuestionsTheme colorScheme={colorScheme}>
        <form className={classes.tile} onSubmit={logIn}>
          <Stack gap="xl">
            <Stack gap="xs">
              <Title order={2}>Log in with a one-time code</Title>
              <Text c="dimmed">{accessCodeIntro(access?.flow)}</Text>
            </Stack>

            <Stack gap="sm">
              <Text className={classes.step}>1. Get your code</Text>
              <Group grow gap="sm">
                {/* Mantine already renders type="button"; said here so these never submit the login form around them. */}
                <Button
                  type="button"
                  variant={sentBy ? 'default' : 'filled'}
                  leftSection={<FontAwesomeIcon icon={faEnvelope} />}
                  loading={sending === 'email'}
                  disabled={!!sending}
                  onClick={() => requestCode('email')}
                >
                  {sentBy === 'email' ? 'Resend email' : 'Email me a code'}
                </Button>
                <Button
                  type="button"
                  variant="default"
                  leftSection={<FontAwesomeIcon icon={faMobileScreen} />}
                  loading={sending === 'phone'}
                  disabled={!!sending || noPhone}
                  onClick={() => requestCode('phone')}
                >
                  {sentBy === 'phone' ? 'Resend text' : 'Text me a code'}
                </Button>
              </Group>
              {sentBy && access && (
                <Alert color="green" variant="light" role="status">
                  {codeSentMessage(sentBy, access)}
                </Alert>
              )}
              {noPhone && (
                <Text size="sm" c="dimmed">
                  There is no phone number on file, so the code comes by email.
                </Text>
              )}
            </Stack>

            <Stack gap="sm">
              <Text className={classes.step}>2. Enter the code</Text>
              <PinInput
                className={classes.code}
                length={CODE_LENGTH}
                type="number"
                inputMode="numeric"
                oneTimeCode
                size="lg"
                ariaLabel="One-time code"
                value={code}
                onChange={setCode}
              />
              <Button
                type="submit"
                size="md"
                fullWidth
                loading={loggingIn}
                disabled={code.length < CODE_LENGTH}
              >
                Log in
              </Button>
            </Stack>

            <Text size="sm" c="dimmed">
              Didn&apos;t get a code? Check your spam folder, or write to{' '}
              <Anchor href="mailto:support@cowbellcyber.ai" size="sm">
                support@cowbellcyber.ai
              </Anchor>
              .
            </Text>
          </Stack>
        </form>
      </BrokerQuestionsTheme>
    </main>
  );
}
